import { useEffect, useState } from 'react';
import { mark } from '../../perf';

// @google/model-viewer bundles its own copy of three.js and costs ~213 KB
// gzipped -- about a third of the initial chunk. Nothing paints until it has
// parsed, and it builds a WebGLRenderer while the module evaluates, so a
// machine without WebGL used to take the whole app down with it. Loading it on
// demand keeps it out of the first paint and contains that failure.
//
// The import points at the published `dist` bundle rather than the bare
// package name on purpose. model-viewer's `module` field is the unbundled
// `lib/`, which imports `three` as a peer (^0.183) -- and when `three` is not
// a direct dependency the bundler walks up and resolves whatever copy it finds
// in a parent directory. In a worktree that is the main checkout's `three`
// 0.133, which fails with a bare "AgXToneMapping is not exported" rather than
// anything about a missing peer. `dist` carries its own three, so there is one
// version and no silent wrong answer. Measured: 289 kB gzipped against 291 kB
// for lib + three, i.e. the choice costs nothing in bundle size.
let pending = null;

export function loadModelViewer() {
  if (!pending) {
    mark('viewer:import:start');
    pending = import(
      '@google/model-viewer/dist/model-viewer.min.js'
    ).then((mod) => {
      mark('viewer:import:end');
      return mod;
    });
  }
  return pending;
}

// True once <model-viewer> is registered. The element upgrades itself when the
// module lands, so the JSX below can stay exactly as it was -- this hook only
// exists to re-render once, and to give callers something to branch on.
export function useModelViewerReady() {
  const [ready, setReady] = useState(
    () =>
      typeof window !== 'undefined' &&
      !!window.customElements &&
      !!window.customElements.get('model-viewer')
  );

  useEffect(() => {
    if (ready) return undefined;

    let cancelled = false;
    loadModelViewer()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((err) => {
        // A viewer that cannot load must not blank the page around it.
        console.error('model-viewer failed to load', err);
      });

    return () => {
      cancelled = true;
    };
  }, [ready]);

  return ready;
}
